/**
 * Qawaem command bar — bottom-center composer (Cursor-style, not chatbot)
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAIAgent, useAIAgentChat } from '../contexts/AIAgentContext';
import aiAgentService from '../services/aiAgentService';
import { isAgentWorkspacePath } from '../config/modules';
import AIAgentMessageThread from './AIAgentMessageThread';
import AIAgentComposer from './AIAgentComposer';
import AIAgentDocumentFormModal from './AIAgentDocumentFormModal';
import AIAgentEngineStatus from './AIAgentEngineStatus';

export default function AIAgentPanel() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.panel.${key}`, opts);
  const wp = (key, opts) => t(`modules.shared.aiAgent.workspacePage.${key}`, opts);
  const location = useLocation();
  const { isOpen, openAgent, closeAgent } = useAIAgent();
  const chat = useAIAgentChat();
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    contextLabel,
    activeModule,
    isReady,
    licenseBlocked,
    entries,
    inputMessage,
    setInputMessage,
    isProcessing,
    pendingConfirmation,
    pendingPlan,
    isBuildingPlan,
    inputRef,
    isVoiceSupported,
    isListening,
    isTranscribing,
    recordingMs,
    audioLevels,
    voiceError,
    isVoiceSession,
    handleSend,
    handleConfirm,
    handleCancel,
    handleBuildPlan,
    handleCancelPlan,
    onInputKeyDown,
    handleVoiceStart,
    handleVoiceStop,
    handleVoiceCancel,
    stopListening,
    cancelListening,
    agentMode,
    setAgentMode,
    pendingAttachment,
    attachmentError,
    handleAttachmentSelect,
    handleAttachmentClear,
    handleAttachmentDrop,
    handleOpenDocumentHandoff,
    handleOpenDocumentEditor,
    handleDraftQuickAction,
    activeDocumentDraft,
    isDocumentFormModalOpen,
    handleCloseDocumentFormModal,
    handleDocumentSaved,
  } = chat;

  const hasOutput = entries.length > 0 || pendingConfirmation || pendingPlan || licenseBlocked;

  useEffect(() => {
    const main = document.querySelector('main');
    if (!main) return undefined;

    if (isOpen) {
      main.style.paddingBottom = '6.5rem';
    } else {
      main.style.paddingBottom = '';
    }

    return () => {
      main.style.paddingBottom = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && isVoiceSession) {
      cancelListening();
    }
  }, [isOpen, isVoiceSession, cancelListening]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen, inputRef]);

  useEffect(() => {
    if (isOpen && isReady) {
      aiAgentService.syncVectorIndexInBackground().catch(() => {});
    }
  }, [isOpen, isReady]);

  useEffect(() => {
    if (entries.length > 0) {
      setIsExpanded(true);
    }
  }, [entries.length]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openAgent();
        setIsExpanded(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape' && isOpen) {
        if (isListening) {
          cancelListening();
        } else {
          stopListening();
        }
        setIsExpanded(false);
        closeAgent();
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openAgent, closeAgent, isOpen, stopListening, cancelListening, isListening, inputRef]);

  if (isAgentWorkspacePath(location.pathname)) {
    return null;
  }

  const composerClass = [
    'pointer-events-auto rounded-2xl border bg-slate-900/95 backdrop-blur-xl overflow-hidden transition-shadow duration-200',
    isVoiceSession
      ? 'border-blue-400/35 shadow-[0_0_0_1px_rgba(59,130,246,0.15),0_20px_56px_rgba(0,0,0,0.4)]'
      : 'border-slate-400/20 shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_16px_48px_rgba(0,0,0,0.35)] focus-within:border-blue-400/35 focus-within:shadow-[0_0_0_1px_rgba(59,130,246,0.2),0_20px_56px_rgba(0,0,0,0.4)]',
    isExpanded && hasOutput ? 'max-h-[min(52vh,420px)] flex flex-col' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="ai-agent-dock"
          className="fixed left-1/2 bottom-5 -translate-x-1/2 z-[9990] w-[min(720px,calc(100vw-2rem))] pointer-events-none font-inherit max-sm:w-[calc(100vw-1rem)] max-sm:bottom-2.5"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <div className={composerClass}>
            <AnimatePresence initial={false}>
              {isExpanded && hasOutput && (
                <motion.div
                  className="border-b border-white/10 overflow-hidden shrink min-h-0 flex flex-col"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
                    <span className="text-[0.68rem] font-semibold tracking-wide uppercase text-slate-400 px-1.5 py-0.5 rounded bg-white/5">
                      {contextLabel}
                    </span>
                    <AIAgentEngineStatus />
                    {entries.length > 0 && (
                      <span className="text-[0.68rem] text-slate-500">
                        {entries.length}{' '}
                        {entries.length > 1 ? p('entries_other') : p('entries_one')}
                      </span>
                    )}
                    <button
                      type="button"
                      className="ml-auto border-none bg-transparent text-slate-400 cursor-pointer p-0.5 rounded grid place-items-center hover:bg-white/5 hover:text-slate-200"
                      onClick={() => setIsExpanded(false)}
                      aria-label={p('collapse_aria')}
                    >
                      <ChevronDown size={16} />
                    </button>
                  </div>

                  <AIAgentMessageThread
                    variant="compact"
                    entries={entries}
                    pendingConfirmation={pendingConfirmation}
                    pendingPlan={pendingPlan}
                    isBuildingPlan={isBuildingPlan}
                    isProcessing={isProcessing}
                    agentMode={agentMode}
                    licenseBlocked={licenseBlocked}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    onBuildPlan={handleBuildPlan}
                    onCancelPlan={handleCancelPlan}
                    onOpenDocumentHandoff={handleOpenDocumentHandoff}
                    onOpenDocumentEditor={handleOpenDocumentEditor}
                    onDraftQuickAction={handleDraftQuickAction}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="relative px-2.5 pt-2 pb-2">
              {!isExpanded && !isVoiceSession && (
                <button
                  type="button"
                  className="absolute -top-2 left-1/2 -translate-x-1/2 w-7 h-[18px] border border-slate-400/20 border-b-0 rounded-t-lg bg-slate-900/95 text-slate-400 cursor-pointer grid place-items-center p-0"
                  onClick={() => {
                    setIsExpanded(true);
                    inputRef.current?.focus();
                  }}
                  aria-label={p('expand_aria')}
                >
                  <ChevronUp size={14} />
                </button>
              )}

              <AIAgentComposer
                variant="compact"
                inputRef={inputRef}
                inputMessage={inputMessage}
                setInputMessage={setInputMessage}
                isProcessing={isProcessing}
                isReady={isReady}
                isVoiceSupported={isVoiceSupported}
                isVoiceSession={isVoiceSession}
                isListening={isListening}
                isTranscribing={isTranscribing}
                recordingMs={recordingMs}
                audioLevels={audioLevels}
                voiceError={voiceError}
                onInputKeyDown={onInputKeyDown}
                onFocus={() => setIsExpanded(true)}
                onSend={handleSend}
                onVoiceStart={() => {
                  setIsExpanded(true);
                  handleVoiceStart();
                }}
                onVoiceStop={handleVoiceStop}
                onVoiceCancel={handleVoiceCancel}
                contextCaption={wp('context_caption', { module: contextLabel })}
                placeholder={activeModule?.placeholder}
                agentMode={agentMode}
                onAgentModeChange={setAgentMode}
                pendingAttachment={pendingAttachment}
                attachmentError={attachmentError}
                onAttachmentSelect={handleAttachmentSelect}
                onAttachmentClear={handleAttachmentClear}
                onAttachmentDrop={handleAttachmentDrop}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    <AIAgentDocumentFormModal
      open={isDocumentFormModalOpen}
      draft={activeDocumentDraft}
      onClose={handleCloseDocumentFormModal}
      onDocumentSaved={handleDocumentSaved}
    />
    </>
  );
}
